import React from 'react';
import { useNavigate } from 'react-router-dom';

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <section className="bg-white py-16 md:py-24 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Left Side - Image/Visual */}
          <div className="relative">
            <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-8 aspect-square flex items-center justify-center">
              <div className="absolute inset-0 opacity-5"></div>
              
              {/* Animated Stats Cards */}
              <div className="relative z-10 space-y-4 w-full max-w-md">
                <div className="bg-white rounded-xl p-6 shadow-lg transform -rotate-3 hover:rotate-0 transition-transform">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600 text-sm">Fully Furnished</span>
                    <div className="w-10 h-10 bg-furniflex-teal rounded-lg flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-gray-900">100%</div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg transform rotate-2 hover:rotate-0 transition-transform ml-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600 text-sm">Happy Owners</span>
                    <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-gray-900">98%</div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg transform -rotate-1 hover:rotate-0 transition-transform">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600 text-sm">Happy Tenants</span>
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-gray-900">98%</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="space-y-6">
            <div>
              <span className="inline-block px-4 py-2 bg-furniflex-teal/10 text-furniflex-teal rounded-full text-sm font-semibold uppercase tracking-wide mb-4">
                About FURNIFLEX
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
                Building Trust Through
                <span className="block text-furniflex-teal">Excellence Every Day</span>
              </h2>
              <div className="w-20 h-1 bg-furniflex-teal mb-6"></div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed">
              Welcome to FURNIFLEX! We transform property management into a hassle-free, profitable experience for 
              owners and a comfortable living experience for tenants. Our professional management ensures properties 
              thrive while owners enjoy peace of mind and tenants enjoy beautifully furnished homes. We offer 
              comprehensive services at competitive rates, serving both property owners and tenants.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed">
              Owners benefit from guaranteed, on-time rental payments while tenants enjoy move-in ready homes with complete support. 
              Your investment is protected, your income is guaranteed, and your peace of mind is our priority.
            </p>

            {/* Values */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-furniflex-teal rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Reliability</h4>
                  <p className="text-sm text-gray-600">Consistent, dependable service you can count on</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-furniflex-teal rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Expertise</h4>
                  <p className="text-sm text-gray-600">Years of industry knowledge and experience</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-furniflex-teal rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Transparency</h4>
                  <p className="text-sm text-gray-600">Clear communication and honest reporting</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-furniflex-teal rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Innovation</h4>
                  <p className="text-sm text-gray-600">Cutting-edge solutions for modern challenges</p>
                </div>
              </div>
            </div>

            <button 
              onClick={() => navigate('/about')}
              className="mt-6 px-8 py-4 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
            >
              Learn More About Us
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
